import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  outlinedInputClasses,
} from "@mui/material";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  chain,
  debounce,
  isArray,
  isEmpty,
  isFunction,
  isNil,
  isString,
  throttle,
  uniqBy,
} from "lodash-es";
import React, {
  DependencyList,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

import { ArrayHelper } from "@/common/helpers/array";
import { AxiosHelper } from "@/common/helpers/axios";
import { PaginationHelper } from "@/common/helpers/pagination";
import { TypeHelper } from "@/common/helpers/type";
import { useEffectWithDeepCompare } from "@/common/hooks/effect/useEffectWithDeepCompare";
import { useMemoWithDeepCompare } from "@/common/hooks/memo/useMemoWithDeepCompare";
import {
  AutocompleteOption,
  AutocompleteOptionType,
  EntitiesToAutocompleteOptionsFunc,
  EntityToAutocompleteOptionFunc,
  createOptionFromInputValue,
} from "@/common/ts/autocomplete";
import { PaginationDtoOfTItemTyped } from "@/common/ts/pagination";
import { BaseEntityDto } from "@/core/api/generated/v1";

import AppIconButton, { AppIconButtonProps } from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import AppTooltip from "../../Tooltip/AppTooltip";
import {
  AutocompleteGroupByFunc,
  AutocompleteRenderGroupTitleParams,
  AutocompleteSortByFunc,
  AutocompleteSortOrder,
  BaseRequestParams,
  EntitySearchRequestFuncParameters,
  EntitySearchResponseData,
  sortOptions,
} from "./BaseEntitySearchAutocomplete";

export type EntitiesSearchResponseData<TEntity> = TEntity[] | PaginationDtoOfTItemTyped<TEntity>;

export type EntitiesSearchRequestFuncParameters<
  TEntity,
  TRequestFunc extends (
    requestParameters: any,
    options?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
> = Parameters<TRequestFunc>[0] & BaseRequestParams;

export interface BaseEntitiesSearchAutocompleteProps<
  TEntity extends BaseEntityDto,
  TRequestFunc extends (
    ...args: any
  ) => Promise<AxiosResponse<EntitiesSearchResponseData<TEntity>>>,
  TRequestParams extends EntitySearchRequestFuncParameters<TEntity, TRequestFunc> =
    EntitySearchRequestFuncParameters<TEntity, TRequestFunc>,
> {
  // generic props
  entityIds: string[] | null | undefined;
  entities: TEntity[] | undefined;
  entityToOption: EntityToAutocompleteOptionFunc<TEntity>;
  /** Load data even if the autocomplete is closed.*/
  isPreload: boolean;
  /** Auto-select single option if the input is required and not disabled. */
  isAutoSelectSingleOption?: boolean;
  /** Auto-select first option if the input is required and not disabled. */
  isAutoSelectFirstOption?: boolean;
  /** Hack to force refetch on deps change by skipping load guards */
  isIgnoreCanLoadCondition?: boolean;
  /** Request configuration. */
  request: {
    requestFunc: TRequestFunc;
    limit?: number;
    parameters: TRequestParams;
    combineParameters: (params: TRequestParams, newParams: BaseRequestParams) => TRequestParams;
    deps: DependencyList;
    skip?: boolean;
  };
  /** Additional request configuration. */
  requestConfig?: {
    limit?: number;
    skip?: boolean;
  };

  // component props
  renderOption: (
    props: HTMLAttributes<HTMLLIElement> & { key: string },
    option: AutocompleteOption<TEntity>,
  ) => ReactNode;
  renderGroupTitle?: (params: AutocompleteRenderGroupTitleParams<TEntity>) => ReactNode;

  endAdornmentActions?: {
    /** @default true */
    if?: boolean;
    actions: Array<{
      /** @default true */
      if?: boolean;
      renderAction: (params: {
        actionIndex: number;
        entities: Nil<TEntity[]>;
        iconButtonProps: Partial<AppIconButtonProps>;
        handleEntitiesSelected: (newEntities: TEntity[]) => void;
      }) => ReactNode;
    }>;
  };
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /** If `true`, hide the selected options from the list box. */
  filterSelectedOptions?: boolean;
  label: TextFieldProps["label"];
  placeholder: TextFieldProps["placeholder"];
  sortBy?: AutocompleteSortByFunc<TEntity>;
  sortOrder?: AutocompleteSortOrder;
  groupBy?: AutocompleteGroupByFunc<TEntity>;
  withCreate?: boolean;
  createOptionTitle?: string | ((inputValue: string) => string) | null;
  textFieldProps?: TextFieldProps;
  /** Only for debugging.*/
  debugKey?: string;
  /** If `true`, the entity option will be disabled. */
  shouldDisable?: (entity: TEntity) => boolean;
  /** If `true`, the entity option will be hidden. */
  shouldHide?: (entity: TEntity) => boolean;
  onChange?: (newEntities?: TEntity[]) => void;
  /** Works only if withCreate is true. */
  onCreate?: (newOption?: AutocompleteOption<TEntity>) => void;
  sx?: SxProps<Theme>;
}

/** Interface to be used by wrapper components that utilize this component. */
export interface BaseEntitiesSearchAutocompleteInheritableProps<TEntity extends BaseEntityDto> {
  entityIds: string[] | null | undefined;
  entities: TEntity[] | undefined;
  /** Load data even if the autocomplete is closed.*/
  isPreload?: boolean;
  /** Auto-select single option if the input is required and not disabled. */
  isAutoSelectSingleOption?: boolean;
  /** Auto-select first option if the input is required and not disabled. */
  isAutoSelectFirstOption?: boolean;
  /** Additional request configuration. */
  requestConfig?: {
    limit?: number;
    skip?: boolean;
  };
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: TextFieldProps["label"];
  /** If `true`, hide the selected options from the list box. */
  filterSelectedOptions?: boolean;
  withCreate?: boolean;
  createOptionTitle?: string | ((inputValue: string | null | undefined) => string) | null;
  textFieldProps?: TextFieldProps;
  /** Only for debugging.*/
  debugKey?: string;
  /** If `true`, the entity option will be disabled. */
  shouldDisable?: (entity: TEntity) => boolean;
  /** If `true`, the entity option will be hidden. */
  shouldHide?: (entity: TEntity) => boolean;
  onChange?: (newEntities?: TEntity[]) => void;
  onCreate?: (newOption?: AutocompleteOption<TEntity>) => void;
}

export default function BaseEntitiesSearchAutocomplete<
  TEntity extends BaseEntityDto,
  TRequestFunc extends (
    requestParameters: any,
    options?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
  TRequestParams extends EntitiesSearchRequestFuncParameters<TEntity, TRequestFunc> =
    EntitySearchRequestFuncParameters<TEntity, TRequestFunc>,
>({
  entityIds,
  entities,
  entityToOption,
  isPreload = true,
  isAutoSelectSingleOption = true,
  isAutoSelectFirstOption = false,
  isIgnoreCanLoadCondition = false,
  request,
  requestConfig,
  renderOption,
  renderGroupTitle,
  endAdornmentActions,
  disabled,
  required,
  size,
  fullWidth,
  filterSelectedOptions = false,
  label,
  placeholder,
  sortBy,
  sortOrder,
  groupBy,
  withCreate,
  createOptionTitle,
  textFieldProps,
  debugKey,
  shouldDisable,
  shouldHide,
  onChange,
  onCreate,
  sx,
  ...otherProps
}: BaseEntitiesSearchAutocompleteProps<TEntity, TRequestFunc>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly AutocompleteOption<TEntity>[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<AutocompleteOption<TEntity>[] | null>(
    null,
  );

  const [inputValue, setInputValue] = useState<string | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResponseData, setSearchResponseData] = useState<
    EntitiesSearchResponseData<TEntity> | undefined
  >(undefined);
  const [isTextFieldHovered, setIsTextFieldHovered] = useState<boolean>(false);

  const isRequestStartedRef = useRef(false);
  const isAutoSelectedRef = useRef(false);

  // just track 401 and 403 to show error to user but do not disable the input to allow user to refresh it manually
  // or in case the next request will succeed
  const axiosErrorRef = useRef<AxiosError>(null);
  const isUnauthenticated = axiosErrorRef.current?.response?.status === 401;
  const isForbidden = axiosErrorRef.current?.response?.status === 403;

  const isSelectedOptionsLoaded =
    isEmpty(entityIds) ||
    (!isEmpty(entityIds) && entityIds!.every((entityId) => options.some((x) => x.id === entityId)));
  const canLoad =
    ((isPreload || (!isPreload && open)) && (isEmpty(selectedOptions) || !isNil(inputValue))) ||
    !isSelectedOptionsLoaded;
  const disabledComputed = disabled;

  const optionsGroupedMap = useMemo(
    () =>
      options &&
      chain(options)
        .groupBy((x) => x.groupBy)
        .value(),
    [options],
  );
  const selectedOptionIds = useMemo(() => selectedOptions?.map((x) => x.id), [selectedOptions]);

  const entityList = useMemo(
    () => (searchResponseData && isArray(searchResponseData) ? searchResponseData : undefined),
    [searchResponseData],
  );
  const paginatedEntities = useMemo(
    () => (searchResponseData && !isArray(searchResponseData) ? searchResponseData : undefined),
    [searchResponseData],
  );
  const entityListComputed = useMemo(
    () => entityList || paginatedEntities?.items || undefined,
    [entityList, paginatedEntities],
  );

  const entitiesToOptions = useMemo<EntitiesToAutocompleteOptionsFunc<TEntity>>(
    () => (entities2) => (entities2 && entities2.map((x) => entityToOption(x))) || [],
    [entityToOption],
  );

  const requestComputed = useMemo<typeof request>(
    () => ({
      ...request,
      ...requestConfig,
      limit: requestConfig?.limit ?? request.limit ?? PaginationHelper.defaultLimit,
      skip: request.skip || requestConfig?.skip,
    }),
    [request, requestConfig],
  );

  const currentLimitRef = useRef<number | undefined>(requestComputed.limit);

  const computeParameters = useCallback(
    (limit?: number, searchValue?: string | null, ids?: string[] | null) => {
      const baseSearchParams: BaseRequestParams = {
        limit: limit,
        search: searchValue || undefined,
        includeIds: ids ? [...ids] : undefined,
      };
      return requestComputed.combineParameters(requestComputed.parameters, baseSearchParams);
    },
    [requestComputed.parameters],
  );

  // compute request params
  const parametersComputed = useMemoWithDeepCompare(() => {
    return computeParameters(currentLimitRef.current, inputValue, entityIds);
  }, [entityIds, inputValue, currentLimitRef.current]);

  const searchRef = useRef(requestComputed.requestFunc);
  const searchComputed = useCallback(async (requestParams: TRequestParams) => {
    if (isRequestStartedRef.current) {
      return;
    }

    isRequestStartedRef.current = true;
    setIsLoading(true);

    try {
      const response = await searchRef.current(requestParams);
      axiosErrorRef.current = null;
      setSearchResponseData(response.data);
    } catch (e) {
      axiosErrorRef.current = AxiosHelper.getAxiosErrorOrNull(e);
      setSearchResponseData(undefined);
    } finally {
      isRequestStartedRef.current = false;
      setIsLoading(false);
    }
  }, []);
  const searchThrottle = useCallback(
    throttle(searchComputed, 500, { leading: true, trailing: false }),
    [searchComputed],
  );
  const searchDebounce = useCallback(
    debounce(searchComputed, 500, { leading: false, trailing: true }),
    [searchComputed],
  );

  // refetch on deps change
  useEffectWithDeepCompare(() => {
    if (!isIgnoreCanLoadCondition && !canLoad) {
      return;
    }
    if (requestComputed.skip) {
      return;
    }
    if (isRequestStartedRef.current) {
      return;
    }
    currentLimitRef.current = requestComputed.limit;
    const params = computeParameters(currentLimitRef.current, inputValue, entityIds);
    searchThrottle(params);
  }, [requestComputed.skip, isIgnoreCanLoadCondition, canLoad, ...requestComputed.deps]);

  // refetch on search input
  useEffect(() => {
    if (!canLoad) {
      return;
    }
    if (requestComputed.skip) {
      return;
    }
    if (isRequestStartedRef.current) {
      return;
    }
    currentLimitRef.current = requestComputed.limit;
    const params = computeParameters(currentLimitRef.current, inputValue, entityIds);
    searchDebounce(params);
  }, [inputValue]);

  useEffect(() => {
    const newOptions = uniqBy(
      [...(selectedOptions ? selectedOptions : []), ...entitiesToOptions(entityListComputed || [])],
      (x) => x.id,
    );
    setOptions(sortOptions({ options: newOptions, sortBy, sortOrder }));

    if (
      !isEmpty(entityIds) &&
      (isEmpty(selectedOptions) ||
        !entityIds!.every((entityId) => selectedOptions!.some((option) => option.id === entityId)))
    ) {
      setSelectedOptions(newOptions.filter((x) => entityIds!.includes(x.id)) || null);
    }

    // handle isAutoSelectSingleOption, isAutoSelectFirstOption
    const singleOption = newOptions.length === 1 ? newOptions[0] : undefined;
    const firstOption = newOptions.length >= 1 ? newOptions[0] : undefined;
    const canAutoSelectOption =
      required &&
      !disabledComputed &&
      !isRequestStartedRef.current &&
      !isLoading &&
      ArrayHelper.isEmpty(entityIds) &&
      ArrayHelper.isEmpty(entities) &&
      ArrayHelper.isEmpty(selectedOptions) &&
      !inputValue &&
      !isAutoSelectedRef.current;
    if (isAutoSelectSingleOption && canAutoSelectOption && singleOption) {
      setSelectedOptions([singleOption]);
      onChange && onChange(singleOption.data ? [singleOption.data] : undefined);
      isAutoSelectedRef.current = true;
    } else if (isAutoSelectFirstOption && canAutoSelectOption && firstOption) {
      setSelectedOptions([firstOption]);
      onChange && onChange(firstOption.data ? [firstOption.data] : undefined);
      isAutoSelectedRef.current = true;
    }
  }, [entityListComputed, entityIds, selectedOptions]);

  // update selected options when entities/entityIds change from outside
  useEffect(() => {
    if (isEmpty(entities) && isEmpty(entityIds)) {
      setSelectedOptions(null);
      // setInputValue(undefined); // NB: this break create new option when searching
    } else if (
      !isEmpty(entities) &&
      (isEmpty(selectedOptionIds) ||
        //!entities.every((entity) => selectedOptionIds?.includes(entity.id || "")) ||
        !ArrayHelper.haveSameElements(
          entities?.map((x) => x.id || ""),
          selectedOptionIds,
        ))
    ) {
      setOptions(
        sortOptions({
          options: uniqBy([...entitiesToOptions(entities), ...options], (x) => x.id),
          sortBy,
          sortOrder,
        }),
      );
      setSelectedOptions(entitiesToOptions(entities));
    } else if (
      !isEmpty(entityIds) &&
      (isEmpty(selectedOptionIds) || !ArrayHelper.haveSameElements(entityIds, selectedOptionIds))
    ) {
      setSelectedOptions(options.filter((x) => entityIds?.includes(x.id)));
    }
  }, [entityIds, entities, selectedOptionIds, options]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage:
      (paginatedEntities?.pagination?.limit || 0) <
      (paginatedEntities?.pagination?.totalCount || 0),
    onLoadMore: () => {
      currentLimitRef.current = (currentLimitRef.current || 0) + (requestComputed.limit || 0);
      searchComputed(parametersComputed);
    },
    rootMargin: "0px 0px 300px 0px",
  });

  const sentryOption: AutocompleteOption<TEntity> = useMemo(
    () => ({ id: "sentry", title: "", optionType: AutocompleteOptionType.Sentry }),
    [],
  );
  return (
    <Autocomplete
      sx={{ minWidth: 200, flex: 1, ...sx }}
      multiple
      fullWidth={fullWidth}
      size={size}
      disabled={disabledComputed}
      filterSelectedOptions={filterSelectedOptions}
      open={open}
      options={options}
      loading={isLoading}
      autoComplete
      openOnFocus
      blurOnSelect={false}
      disableCloseOnSelect
      includeInputInList
      value={selectedOptions || []}
      inputValue={inputValue || ""}
      selectOnFocus
      clearOnBlur={false}
      clearOnEscape
      handleHomeEndKeys
      freeSolo={withCreate}
      ListboxProps={{ ref: rootRef }}
      groupBy={groupBy}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => {
        // value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // regular option
        return option.title;
      }}
      filterOptions={(opts, params) => {
        // suggest the creation of a new value (add dynamic option with input value)
        if (withCreate) {
          const isExisting = options.some((option) => params.inputValue === option.title);
          if (params.inputValue !== "" && !isExisting) {
            const title =
              (createOptionTitle &&
                ((isFunction(createOptionTitle) && createOptionTitle(params.inputValue)) ||
                  (isString(createOptionTitle) && createOptionTitle))) ||
              `Create new "${params.inputValue}"`;

            opts.push(
              createOptionFromInputValue({
                inputValue: params.inputValue,
                title: title,
              }),
            );
          }
        }

        if (
          !isEmpty(opts) &&
          (paginatedEntities?.pagination?.limit || 0) <
            (paginatedEntities?.pagination?.totalCount || 0)
        ) {
          // add sentry option for infinite scroll
          opts.push(sentryOption);
        }

        return opts;
      }}
      onChange={(e, newValues) => {
        const newOptions = newValues.filter((x) => !isString(x)) as AutocompleteOption<TEntity>[];
        const newOptionsFromInputValue = newOptions.filter((x) => !!x.inputValue);
        const newOptionFromInputValue =
          newOptionsFromInputValue.length !== 0 ? newOptionsFromInputValue[0] : undefined;
        const newInputValues = newValues.filter((x) => isString(x)) as string[];
        const newInputValue = newInputValues.length !== 0 ? newInputValues[0] : undefined;

        // value selected with enter, right from the input
        if (typeof newInputValue === "string") {
          withCreate &&
            onCreate &&
            onCreate(
              createOptionFromInputValue({
                inputValue: newInputValue,
              }),
            );
          return;
        } else if (newOptionFromInputValue && newOptionFromInputValue.inputValue) {
          // create a new value from the user input
          setInputValue("");
          withCreate && onCreate && onCreate(newOptionFromInputValue);
          return;
        } else {
          setSelectedOptions(newOptions);
          setInputValue("");
          onChange &&
            onChange(newOptions.length !== 0 ? newOptions.map((x) => x.data!) : undefined);
        }
      }}
      onInputChange={(e, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <FormControl fullWidth>
          <TextField
            {...textFieldProps}
            {...params}
            sx={{
              ...textFieldProps?.sx,

              // show action on hover
              "& .BaseEntitySearchAutocomplete-endAdornment-action": {
                visibility: "hidden",
              },
              "&:hover .BaseEntitySearchAutocomplete-endAdornment-action": {
                visibility: "visible",
              },
              [`& .${outlinedInputClasses.focused} .BaseEntitySearchAutocomplete-endAdornment-action`]:
                {
                  visibility: "visible",
                },
            }}
            required={required}
            label={label || textFieldProps?.label}
            placeholder={placeholder || textFieldProps?.placeholder}
            fullWidth
            onMouseEnter={() => setIsTextFieldHovered(true)}
            onMouseLeave={() => setIsTextFieldHovered(false)}
            InputProps={{
              ...textFieldProps?.InputProps,
              ...params.InputProps,
              startAdornment: (
                <>
                  {textFieldProps?.InputProps?.startAdornment}

                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {/* Actions */}
                  {TypeHelper.isNilOrTrue(endAdornmentActions?.if) &&
                    endAdornmentActions?.actions
                      .filter((action) => TypeHelper.isNilOrTrue(action.if))
                      .map((action, actionIndex) => (
                        <React.Fragment key={actionIndex}>
                          {action.renderAction({
                            actionIndex,
                            entities: selectedOptions?.map((x) => x.data!).filter(Boolean),
                            iconButtonProps: {
                              className: "BaseEntitySearchAutocomplete-endAdornment-action",
                              disabled: disabledComputed,
                              size: "small",
                              shape: "round",
                              onClick: (e) => {
                                e.stopPropagation(); // prevent autocomplete thinking it's open
                              },
                            },
                            handleEntitiesSelected: (newEntities) => {
                              const newOptions = newEntities.map((x) => entityToOption(x));
                              setSelectedOptions(newOptions);
                              onChange &&
                                onChange(
                                  newOptions.map((x) => x.data!).filter(Boolean) || undefined,
                                );
                            },
                          })}
                        </React.Fragment>
                      ))}

                  {/* Refresh */}
                  {isLoading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : (
                    <AppIconButton
                      title='Refresh'
                      // using visibility like Mui clear button
                      sx={{ visibility: isTextFieldHovered ? "visible" : "hidden" }}
                      disabled={disabledComputed}
                      size='small'
                      onClick={() => searchComputed(parametersComputed)}
                    >
                      <AppIcon of='refresh' />
                    </AppIconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />

          {isUnauthenticated && (
            <FormHelperText error>
              {`You aren't authenticated`}{" "}
              <AppTooltip title='This might be a transient error, or your session has expired. Please try to refresh the input, refresh the page or re-login.'>
                <AppIcon of='help' inText />
              </AppTooltip>
            </FormHelperText>
          )}
          {isForbidden && (
            <FormHelperText error>
              {`You don't have enough permissions`}{" "}
              <AppTooltip
                title={
                  <Box>
                    <Box>
                      {`You don't have the permissions required to access this resource. Check with
                                your company admin for extended permissions.`}
                    </Box>
                    <Box>
                      This might also be a transient error. You can try to refresh the input,
                      refresh the page, or re-login.
                    </Box>
                  </Box>
                }
              >
                <AppIcon of='help' inText />
              </AppTooltip>
            </FormHelperText>
          )}
        </FormControl>
      )}
      renderGroup={
        groupBy
          ? (params) => {
              const optionsInGroup = optionsGroupedMap[params.group] || [];
              const titleRendered = renderGroupTitle
                ? renderGroupTitle({
                    group: params.group,
                    options: optionsInGroup,
                    props: {},
                  })
                : undefined;

              return (
                <ListItem key={params.key} disablePadding>
                  <Stack sx={{ width: "100%" }}>
                    <ListSubheader component='div' sx={{ position: "sticky", top: "-8px" }}>
                      {titleRendered || params.group}
                    </ListSubheader>
                    <List disablePadding>{params.children}</List>
                  </Stack>
                </ListItem>
              );
            }
          : undefined
      }
      renderOption={(props, option) => {
        if (option.optionType === AutocompleteOptionType.Sentry) {
          return (
            <ListItem ref={sentryRef}>
              <ListItemText>Loading...</ListItemText>
            </ListItem>
          );
        }

        const isHidden = option.data && shouldHide ? shouldHide(option.data) : false;
        if (isHidden) {
          return null;
        }
        const isDisabled = option.data && shouldDisable ? shouldDisable(option.data) : false;
        return renderOption(
          {
            ...props,
            key: option.id,
            style: {
              ...props?.style,
              cursor: "default",
              ...(option.optionType === AutocompleteOptionType.NoOptions
                ? {
                    cursor: "default",
                  }
                : undefined),
              ...(isDisabled
                ? {
                    pointerEvents: "none",
                    opacity: 0.5,
                  }
                : undefined),
            },
          },
          option,
        );
      }}
      {...otherProps}
    />
  );
}
